<script setup lang="ts">
/**
 * Singbac Incorporation Post — 7종 문서를 한 화면에 연속 표시.
 * 회사·client·resident director 정보를 한 번 채우면 여러 문서에 공유(필드 키 공유).
 *
 * 문서 구분:
 *  · 회사 단위(1부): DRIW · ND Indemnity · Notice by Nominee · Undertaking · Structure Chart
 *  · client-이사 단위(N부, DocScope dir_{id}/): NON NOMINEE Notice · Disclosure of Interest
 * → 이사(client-director)가 여러 명이면 상단 "+ Add director" 로 부수를 늘린다
 *   (NON NOMINEE 와 Disclosure 가 같은 스코프 dir_{id}/ 를 공유하므로 client 정보는 한 번만 입력).
 */
import { onMounted, ref, computed, watch } from 'vue';
import DocScope from './cdd/DocScope.vue';
import SingbacDriwDoc from './SingbacDriwDoc.vue';
import SingbacIndemnityDoc from './SingbacIndemnityDoc.vue';
import SingbacNonNomineeNoticeDoc from './SingbacNonNomineeNoticeDoc.vue';
import SingbacNomineeNoticeDoc from './SingbacNomineeNoticeDoc.vue';
import SingbacUndertakingDoc from './SingbacUndertakingDoc.vue';
import SingbacDisclosureDoc from './SingbacDisclosureDoc.vue';
import SingbacStructureChartDoc from './SingbacStructureChartDoc.vue';

/**
 * 노미니 디렉터(L_OFFICER.IS_NOMINEE=1) 유무 — 없으면 노미니 전용 문서
 * (Nominee Director Service & Indemnity, Notice by Nominee Director) 를 기본적으로 렌더/생성하지 않는다.
 * 다만 다른 인코퍼레이션처럼 오퍼레이터가 "+ Add document" 로 필요 시 수동 추가할 수 있다.
 * 패널이 자동 판정값(hasNominee)을 내려주고, 없으면 기본 false(노미니 문서 숨김).
 */
const props = withDefaults(defineProps<{ hasNominee?: boolean }>(), { hasNominee: false });

// ─── 노미니 전용 문서 표시/수동 추가 ───
//  자동 감지(hasNominee) 되면 표시, 아니면 "+ Add nominee" 로 수동 추가. 표시 여부 = 생성 여부.
const manualNominee = ref(false);
const showNominee = computed(() => props.hasNominee || manualNominee.value);
watch(
  () => props.hasNominee,
  (v) => {
    if (v) manualNominee.value = false;
  },
);

/** 문서를 성격별로 묶은 Stage (스텝 형식) */
interface StageDef { no: number; en: string; short: string }
const STAGES: StageDef[] = [
  { no: 1, en: 'Company & Board', short: 'Company' },
  { no: 2, en: 'Resident Director (Singbac)', short: 'Resident Dir' },
  { no: 3, en: 'Nominee Director Notices (s.386AL)', short: 'ND Notices' },
  { no: 4, en: 'Director Disclosures', short: 'Disclosures' },
];
const activeStage = ref(1);

/** client-이사 부수 id 목록 (NON NOMINEE + Disclosure + Undertaking 공유) */
const directorIds = ref<number[]>([1]);
let seq = 1;

function addDirector() {
  directorIds.value.push(++seq);
}
function removeDirector(id: number) {
  if (directorIds.value.length <= 1) return;
  directorIds.value = directorIds.value.filter((x) => x !== id);
}

/** 노미니 이사 부수 id 목록 (Notice by Nominee + ND Indemnity 공유) */
const nomineeIds = ref<number[]>([1]);
let nomSeq = 1;

function addNominee() {
  manualNominee.value = true; // 추가 시 노미니 문서 표시
  nomineeIds.value.push(++nomSeq);
}
function removeNominee(id: number) {
  // 문서 옆 X — 1건이어도 삭제(0 개까지 → 노미니 문서 미생성). 최상단 "+ Add nominee" 로 다시 추가.
  nomineeIds.value = nomineeIds.value.filter((x) => x !== id);
}

// ─── 문서 확대 (시니어 가독성) — localStorage 유지 ───
const ZOOM_KEY = 'singbacPost.docZoom';
const ZOOM_MIN = 100;
const ZOOM_MAX = 175;
const ZOOM_STEP = 25;
function clampZoom(n: number) {
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(n / ZOOM_STEP) * ZOOM_STEP));
}
function readStoredZoom(): number {
  try {
    const n = Number(localStorage.getItem(ZOOM_KEY));
    if (Number.isFinite(n)) return clampZoom(n);
  } catch {
    /* ignore */
  }
  return 100;
}
const docZoom = ref(readStoredZoom());
function setDocZoom(n: number) {
  docZoom.value = clampZoom(n);
  try {
    localStorage.setItem(ZOOM_KEY, String(docZoom.value));
  } catch {
    /* ignore */
  }
}

type LocalState = {
  directorIds?: number[];
  nomineeIds?: number[];
  activeStage?: number;
  manualNominee?: boolean;
  // createTask 가 생성 포함 여부를 읽는 유효 플래그(표시=생성).
  includeIndemnity?: boolean;
  includeNominee?: boolean;
};
const pendingLocal = ref<LocalState | null>(null);
let mounted = false;

function applyLocalState(state: LocalState) {
  if (!state) return;
  if (Array.isArray(state.directorIds)) {
    directorIds.value = [...state.directorIds];
    if (state.directorIds.length) seq = Math.max(...state.directorIds, seq);
  }
  if (Array.isArray(state.nomineeIds)) {
    nomineeIds.value = [...state.nomineeIds];
    if (state.nomineeIds.length) nomSeq = Math.max(...state.nomineeIds, nomSeq);
  }
  if (state.activeStage && state.activeStage >= 1 && state.activeStage <= STAGES.length) {
    activeStage.value = state.activeStage;
  }
  if (typeof state.manualNominee === 'boolean') manualNominee.value = state.manualNominee;
}
function gatherLocalState(): LocalState {
  return {
    directorIds: [...directorIds.value],
    nomineeIds: [...nomineeIds.value],
    activeStage: activeStage.value,
    manualNominee: manualNominee.value,
    includeIndemnity: showNominee.value,
    includeNominee: showNominee.value,
  };
}
function restoreLocalState(state: LocalState) {
  if (!state) return;
  pendingLocal.value = state;
  if (mounted) applyLocalState(state);
}

onMounted(() => {
  mounted = true;
  if (pendingLocal.value) applyLocalState(pendingLocal.value);
});

defineExpose({ gatherLocalState, restoreLocalState });
</script>

<template>
  <div class="allot-pkg singbac-pkg">
    <!-- Stage 스텝퍼 (기존 형식) -->
    <div class="stepper">
      <button
        v-for="st in STAGES"
        :key="st.no"
        type="button"
        class="step"
        :class="{ active: activeStage === st.no, done: activeStage > st.no }"
        :title="`Stage ${st.no} · ${st.en}`"
        @click="activeStage = st.no"
      >
        <span class="step-no">{{ activeStage > st.no ? '✓' : st.no }}</span>
        <span class="step-txt"><b>Stage {{ st.no }}</b><small>{{ st.short }}</small></span>
      </button>
    </div>

    <div class="zoom-bar">
      <div class="doc-zoom" role="group" aria-label="Larger text">
        <button type="button" class="doc-zoom-btn" :disabled="docZoom <= ZOOM_MIN" title="Smaller text" @click="setDocZoom(docZoom - ZOOM_STEP)">A−</button>
        <button type="button" class="doc-zoom-pct" title="Reset size" @click="setDocZoom(100)">{{ docZoom }}%</button>
        <button type="button" class="doc-zoom-btn" :disabled="docZoom >= ZOOM_MAX" title="Larger text" @click="setDocZoom(docZoom + ZOOM_STEP)">A+</button>
      </div>
    </div>

    <!-- Stage 본문 (v-show — 입력 mounted 유지) -->
    <div class="paper-zoom" :style="{ zoom: docZoom / 100 }">
      <!-- Stage 1 · Company & Board -->
      <section v-show="activeStage === 1" class="stage">
        <div class="stage-head"><span class="stage-badge">Stage 1</span><span class="stage-t">Company &amp; Board</span></div>
        <div class="pkg-doc">
          <div class="pkg-doclabel"><span class="ic">⚖️</span><span class="nm">DRIW — First Board Resolutions</span></div>
          <div class="simple-frame"><SingbacDriwDoc /></div>
        </div>
        <div class="pkg-doc">
          <div class="pkg-doclabel"><span class="ic">🗂️</span><span class="nm">Company's Structure Chart</span></div>
          <div class="simple-frame"><SingbacStructureChartDoc /></div>
        </div>
      </section>

      <!-- Stage 2 · Resident Director (Singbac) -->
      <section v-show="activeStage === 2" class="stage">
        <div class="stage-head"><span class="stage-badge">Stage 2</span><span class="stage-t">Resident Director (Singbac)</span></div>

        <!-- 최상단 문서 추가 -->
        <div class="sg-toolbar add-bar">
          <span class="add-lbl">Add document</span>
          <button type="button" class="add-btn" @click="addDirector">+ Client-director</button>
          <button type="button" class="add-btn" @click="addNominee">+ Nominee director</button>
        </div>

        <!-- Undertaking by Client — client-director 명수대로 -->
        <div class="grp-lbl">Undertaking by Client — one per client-director</div>
        <template v-for="(id, i) in directorIds" :key="`undertaking-${id}`">
          <div class="pkg-doc">
            <div class="pkg-doclabel">
              <span class="ic">📄</span><span class="nm">Undertaking by Client (Resident Director)</span>
              <span class="copy">Director #{{ i + 1 }}</span>
              <button v-if="directorIds.length > 1" type="button" class="tb-rm" title="Remove director" @click="removeDirector(id)">×</button>
            </div>
            <div class="simple-frame">
              <DocScope :scope="`dir_${id}/`"><SingbacUndertakingDoc /></DocScope>
            </div>
          </div>
        </template>

        <!-- ND Service & Indemnity — 노미니 명수대로(노미니 있을 때만) -->
        <template v-if="showNominee">
          <div class="grp-lbl">Nominee Director Service &amp; Indemnity — one per nominee director</div>
          <template v-for="(id, i) in nomineeIds" :key="`indemnity-${id}`">
            <div class="pkg-doc">
              <div class="pkg-doclabel">
                <span class="ic">📄</span><span class="nm">Nominee Director Service &amp; Indemnity Agreement</span>
                <span class="copy">Nominee #{{ i + 1 }}</span>
                <button type="button" class="tb-rm" title="Remove nominee" @click="removeNominee(id)">×</button>
              </div>
              <div class="simple-frame">
                <DocScope :scope="`nom_${id}/`"><SingbacIndemnityDoc /></DocScope>
              </div>
            </div>
          </template>
        </template>
      </section>

      <!-- Stage 3 · Nominee Director Notices (s.386AL) -->
      <section v-show="activeStage === 3" class="stage">
        <div class="stage-head"><span class="stage-badge">Stage 3</span><span class="stage-t">Nominee Director Notices (s.386AL)</span></div>

        <!-- 최상단 문서 추가 -->
        <div class="sg-toolbar add-bar">
          <span class="add-lbl">Add document</span>
          <button type="button" class="add-btn" @click="addDirector">+ Client-director</button>
          <button type="button" class="add-btn" @click="addNominee">+ Nominee director</button>
        </div>

        <!-- NON NOMINEE Notice — client-director 명수대로 -->
        <div class="grp-lbl">NON NOMINEE Notice — one per client-director</div>
        <template v-for="(id, i) in directorIds" :key="`nonnom-${id}`">
          <div class="pkg-doc">
            <div class="pkg-doclabel">
              <span class="ic">📄</span><span class="nm">NON NOMINEE Notice</span>
              <span class="copy">Director #{{ i + 1 }}</span>
              <button v-if="directorIds.length > 1" type="button" class="tb-rm" title="Remove director" @click="removeDirector(id)">×</button>
            </div>
            <div class="simple-frame">
              <DocScope :scope="`dir_${id}/`"><SingbacNonNomineeNoticeDoc /></DocScope>
            </div>
          </div>
        </template>

        <!-- Notice by Nominee Director — 노미니 명수대로(노미니 있을 때만) -->
        <template v-if="showNominee">
          <div class="grp-lbl">Notice by Nominee Director — one per nominee director</div>
          <template v-for="(id, i) in nomineeIds" :key="`nomnotice-${id}`">
            <div class="pkg-doc">
              <div class="pkg-doclabel">
                <span class="ic">📄</span><span class="nm">Notice by Nominee Director</span>
                <span class="copy">Nominee #{{ i + 1 }}</span>
                <button type="button" class="tb-rm" title="Remove nominee" @click="removeNominee(id)">×</button>
              </div>
              <div class="simple-frame">
                <DocScope :scope="`nom_${id}/`"><SingbacNomineeNoticeDoc /></DocScope>
              </div>
            </div>
          </template>
        </template>
      </section>

      <!-- Stage 4 · Director Disclosures -->
      <section v-show="activeStage === 4" class="stage">
        <div class="stage-head"><span class="stage-badge">Stage 4</span><span class="stage-t">Director Disclosures</span></div>
        <!-- 최상단 문서 추가 -->
        <div class="sg-toolbar add-bar">
          <span class="add-lbl">Add document</span>
          <button type="button" class="add-btn" @click="addDirector">+ Client-director</button>
        </div>

        <!-- Disclosure of Interest — client-director 명수대로 -->
        <div class="grp-lbl">Disclosure of Interest — one per client-director</div>
        <template v-for="(id, i) in directorIds" :key="`disc-${id}`">
          <div class="pkg-doc">
            <div class="pkg-doclabel">
              <span class="ic">📄</span><span class="nm">Disclosure of Interest (s.165 &amp; s.156)</span>
              <span class="copy">Director #{{ i + 1 }}</span>
              <button v-if="directorIds.length > 1" type="button" class="tb-rm" title="Remove director" @click="removeDirector(id)">×</button>
            </div>
            <div class="simple-frame">
              <DocScope :scope="`dir_${id}/`"><SingbacDisclosureDoc /></DocScope>
            </div>
          </div>
        </template>
      </section>
    </div>
  </div>
</template>

<style scoped>
.allot-pkg { color: #111; }
.zoom-bar { display: flex; justify-content: flex-end; width: 794px; max-width: 100%; margin: 0 auto 14px; box-sizing: border-box; }
.doc-zoom { display: inline-flex; align-items: center; gap: 2px; background: #f2f4f6; border-radius: 10px; padding: 2px; }
.doc-zoom-btn, .doc-zoom-pct { border: none; background: transparent; cursor: pointer; font-weight: 800; color: #4b5563; border-radius: 8px; line-height: 1; }
.doc-zoom-btn { min-width: 36px; height: 32px; font-size: 15px; }
.doc-zoom-btn:hover:not(:disabled) { background: #fff; color: #191f28; }
.doc-zoom-btn:disabled { opacity: 0.35; cursor: default; }
.doc-zoom-pct { min-width: 52px; height: 32px; font-size: 12px; color: #3182f6; }
.paper-zoom { width: 794px; max-width: 100%; margin: 0 auto; }
.pkg-doc { width: 100%; margin: 0 auto; }
.pkg-doc + .pkg-doc { margin-top: 30px; }
.pkg-doclabel { display: flex; align-items: center; gap: 8px; padding: 0 4px 10px; }
.pkg-doclabel .ic { font-size: 14px; line-height: 1; }
.pkg-doclabel .nm { font-size: 12.5px; font-weight: 800; color: #42506a; letter-spacing: 0.01em; }
.pkg-doclabel .copy { font-size: 10.5px; font-weight: 800; color: #1b84ff; background: #eaf3ff; border-radius: 999px; padding: 2px 8px; }
.tb-rm { border: none; background: transparent; color: #c0392b; font-size: 18px; cursor: pointer; line-height: 1; padding: 0 2px; }
.simple-frame { width: 100%; background: #fff; border: 1px solid #e2e5ea; box-shadow: 0 4px 20px rgba(20, 30, 60, 0.09); padding: 32px; box-sizing: border-box; overflow: hidden; font-size: 12px; }
.pkg-add { width: 100%; margin: 0 auto; padding: 14px 16px; border: 1px dashed #cdd7e6; border-radius: 12px; background: #f7f9fd; display: flex; flex-wrap: wrap; gap: 10px; align-items: center; box-sizing: border-box; }
.pkg-add-lbl { font-size: 11.5px; font-weight: 700; color: #6b7a94; }
.pkg-add .sg-tool { margin-left: auto; }

/* Stage 스텝퍼 (기존 Aura 형식) */
.stepper { display: flex; flex-wrap: nowrap; gap: 8px; width: 794px; max-width: 100%; margin: 0 auto 12px; padding: 12px; background: #fff; border: 1px solid #e6ebf3; border-radius: 14px; box-shadow: 0 6px 18px rgba(20, 30, 60, 0.06); box-sizing: border-box; }
.step { flex: 1 1 0; min-width: 0; display: flex; align-items: flex-start; gap: 8px; padding: 10px 10px; border: 1px solid transparent; border-radius: 12px; background: #f6f8fc; cursor: pointer; text-align: left; transition: all 0.15s; }
.step:hover { background: #eef3fb; }
.step.active { background: #eaf1ff; border-color: #b9d0ff; }
.step.done { background: #eafaf1; }
.step-no { flex: none; width: 24px; height: 24px; border-radius: 50%; background: #c9d3e4; color: #fff; font-weight: 800; font-size: 11px; display: inline-flex; align-items: center; justify-content: center; margin-top: 1px; }
.step.active .step-no { background: #1b64da; }
.step.done .step-no { background: #22a06b; }
.step-txt { display: flex; flex-direction: column; gap: 2px; line-height: 1.25; min-width: 0; }
.step-txt b { font-size: 11.5px; color: #1f2d4d; white-space: nowrap; }
.step-txt small { font-size: 10px; color: #7a869c; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.stage { width: 100%; max-width: 100%; margin: 0 auto; }
.stage-head { display: flex; align-items: center; gap: 12px; margin: 0 0 18px; padding: 4px 2px 14px; border-bottom: 1px solid #eef1f6; }
.stage-badge { flex: none; background: #1b64da; color: #fff; font-weight: 800; font-size: 12.5px; padding: 5px 12px; border-radius: 999px; letter-spacing: 0.02em; }
.stage-t { font-weight: 800; font-size: 15px; color: #1f2d4d; }

.sg-toolbar { width: 100%; margin: 0 0 22px; display: flex; flex-wrap: wrap; gap: 10px; align-items: center; padding: 14px 16px; border: 1px solid #d7e0f0; border-radius: 12px; background: #f7f9fd; box-sizing: border-box; }
.sg-toolbar-lbl { font-size: 11.5px; font-weight: 800; color: #6b7a94; margin-right: 6px; }
.grp-lbl { width: 100%; margin: 0 0 12px; padding: 2px 2px 8px; border-bottom: 1px solid #eef1f6; font-size: 12px; font-weight: 800; color: #42506a; letter-spacing: 0.01em; }
.sg-tool { margin-left: auto; border: 1px solid #1b84ff; background: #fff; color: #1b84ff; border-radius: 999px; padding: 7px 13px; font-size: 12px; font-weight: 700; cursor: pointer; }
.sg-tool:hover { background: #eaf3ff; }

/* 최상단 문서 추가 바 — 라벨 왼쪽, 버튼들 오른쪽에 모아서 */
.add-bar { margin: 0 0 18px; padding: 10px 14px; border: 1px solid #e6ebf3; border-radius: 12px; background: #fbfcff; box-shadow: none; gap: 8px; }
.add-bar .add-lbl { font-size: 11.5px; font-weight: 800; color: #8a94a6; margin-right: auto; }
.add-bar .add-btn { border: 1px solid #d5e2ff; background: #fff; color: #1b6fe0; border-radius: 8px; padding: 7px 14px; font-size: 12px; font-weight: 700; cursor: pointer; }
.add-bar .add-btn:hover { background: #eef4ff; border-color: #b9d0ff; }
</style>

<style>
.paper:has(.singbac-pkg) {
  width: 794px !important;
  max-width: 100% !important;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
}
.singbac-pkg .allot-doc { width: 100%; max-width: 100%; box-sizing: border-box; }
.singbac-pkg .cdd-tbl { width: 100% !important; table-layout: fixed !important; box-sizing: border-box; }
.singbac-pkg .cdd-tbl td, .singbac-pkg .cdd-tbl th { box-sizing: border-box; overflow-wrap: break-word; word-break: normal; vertical-align: top; }
.singbac-pkg .lv-field { display: inline-flex !important; max-width: 100%; box-sizing: border-box; white-space: normal !important; flex-wrap: wrap; align-items: baseline; vertical-align: baseline; }
.singbac-pkg .lv-field .lv-text { min-width: 0; overflow-wrap: break-word; word-break: break-word; }
</style>
